/**
 * The interviewers' Lever feedback, for the RECRUITER, on a card where the candidate asked for it.
 *
 * Three things this component is careful about:
 *  - It is labelled INTERNAL and closed by default. It is the one place the tab shows interview
 *    notes, and nothing here is ever sent: the candidate's reply is generated from these notes
 *    through the AGG guardrails (see lib/inboundFeedbackReply.js), never by quoting them.
 *  - Rendered as TEXT. Interview notes are typed by people into a form; they are no more trusted
 *    as markup than a candidate's email is.
 *  - It renders whether or not a draft exists — when nothing survived the guardrails, the note is
 *    the whole point: the recruiter writes the reply themselves, from this.
 */
import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { COLORS } from './format';
import type { InboundRequest } from '../../types/taActionBoard';

export function FeedbackNoteBox({ note }: { note: InboundRequest['feedbackNote'] }) {
  const [open, setOpen] = useState(false);
  if (!note) return null;

  return (
    <div
      className="rounded-lg p-3 text-sm"
      style={{ backgroundColor: COLORS.cotton, color: '#484848' }}
      data-no-toggle
    >
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setOpen((v) => !v)}
        className="!h-auto !p-0 text-xs font-semibold underline"
        data-no-toggle
      >
        {open ? '▾' : '▸'} 🔒 Interviewers’ feedback in Lever — internal, not sent (
        {note.forms === 1 ? '1 form' : `${note.forms} forms`})
      </Button>
      {open && (
        <>
          <p className="mt-2 whitespace-pre-wrap rounded bg-white/70 p-2 text-xs">{note.text}</p>
          {note.truncated && (
            <p className="mt-1 text-xs italic">
              … longer than shown — open the candidate in Lever for the full forms.
            </p>
          )}
          <p className="mt-2 text-xs italic">
            Never paste this into the reply. A feedback draft is written FROM these notes and checked
            against our AGG rules; the notes themselves were not.
          </p>
        </>
      )}
    </div>
  );
}
